/**
 * Carbon radio group: a fieldset with a 12px legend and native radios, so the
 * arrow keys move the choice. `list` (the default) stacks 18px radios with
 * their labels; `tile` puts each choice on a layer-01 tile that takes a 1px
 * ink border when selected (Carbon's selectable tile). The round radio is
 * `.c-radio` in index.css: the Square Rule's sanctioned exception, beside
 * Tag, Avatar and StatusDot (D39).
 */
import { useId, type ReactNode } from 'react'
import clsx from 'clsx'

export interface RadioOption {
  value: string
  label: ReactNode
  helper?: ReactNode
  disabled?: boolean
}

export interface RadioGroupProps {
  /** The radios' shared name (one group per name on the page). */
  name: string
  value: string | null
  onChange: (value: string) => void
  options: RadioOption[]
  legend: ReactNode
  variant?: 'list' | 'tile'
  /** Keep the legend for screen readers only. */
  hideLegend?: boolean
  disabled?: boolean
  className?: string
}

export function RadioGroup({
  name,
  value,
  onChange,
  options,
  legend,
  variant = 'list',
  hideLegend,
  disabled,
  className,
}: RadioGroupProps) {
  const groupId = useId()
  const tile = variant === 'tile'
  return (
    <fieldset disabled={disabled} className={clsx('min-w-0', className)}>
      <legend className={clsx(hideLegend ? 'sr-only' : 'mb-2 text-helper', disabled ? 'text-text-placeholder' : 'text-text-secondary')}>
        {legend}
      </legend>
      <div className={clsx('flex flex-col', tile ? 'gap-2' : 'gap-3')}>
        {options.map((option, i) => {
          const inputId = `${groupId}-${i}`
          const helperId = option.helper ? `${inputId}-helper` : undefined
          const checked = value === option.value
          const off = disabled || option.disabled
          return (
            <label
              key={option.value}
              htmlFor={inputId}
              className={clsx(
                'flex min-w-0 items-start gap-2',
                off ? 'cursor-not-allowed' : 'cursor-pointer',
                tile && 'border bg-layer-1 p-4 transition-colors duration-110 ease-carbon',
                tile && (checked ? 'border-text-primary' : 'border-transparent'),
                tile && !off && 'hover:bg-layer-hover',
              )}
            >
              <input
                id={inputId}
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                disabled={option.disabled}
                aria-describedby={helperId}
                onChange={() => onChange(option.value)}
                className="c-radio"
              />
              <span className="min-w-0">
                <span className={clsx('block text-body', off ? 'text-text-placeholder' : 'text-text-primary', tile && checked && 'font-semibold')}>
                  {option.label}
                </span>
                {option.helper ? (
                  <span id={helperId} className="mt-1 block text-helper text-text-helper">
                    {option.helper}
                  </span>
                ) : null}
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export default RadioGroup
